import { Button, Modal, Steps, Typography } from 'antd'
import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoEntry, GeckoMeasurementJson } from 'types'

import { NiiDocumentTaggingView } from './NiiDocumentTaggingView'
import { NiiFileUploadView } from './NiiFileUploadView'
import { NiiResultsView } from './NiiResultsView'
import { NiiSourcesView } from './NiiSourcesView'

const { Title } = Typography

const StyledButtonDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`

const NII_DETAILS_TABS_MAPPING = [
  {
    title: 'File Upload',
    component: NiiFileUploadView,
  },
  {
    title: 'Document Tagging',
    component: NiiDocumentTaggingView,
  },
  {
    title: 'Data Sources',
    component: NiiSourcesView,
  },
  {
    title: 'Results',
    component: NiiResultsView,
  },
]

export interface NiiViewProps {
  niiId: string
  siteEntry: GeckoEntry
  unitEntry: GeckoEntry
  niiData: NiiData
}

interface NiiDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  niiEntry: GeckoEntry | null
  siteEntry?: GeckoEntry
  unitEntry?: GeckoEntry
}

type NiiSourceParamStringValue = {
  content: string
  file_path: string
}

type NiiSourceParamNumberValue = {
  content: number
  file_path: string
}

export type NiiSourcesParams = {
  commission_date?: NiiSourceParamStringValue
  design_code?: NiiSourceParamStringValue
  contents?: NiiSourceParamStringValue
  external_coating?: NiiSourceParamStringValue
  insulation?: NiiSourceParamStringValue
  design_pressure_bar?: NiiSourceParamNumberValue
  operating_pressure_bar?: NiiSourceParamNumberValue
  design_temperature_c?: NiiSourceParamNumberValue
  max_operating_temperature_c?: NiiSourceParamNumberValue
  internal_inspection_interval_yrs?: NiiSourceParamNumberValue
  next_internal_inspection?: NiiSourceParamStringValue
}

export type NiiSourcesZoneParams = {
  material?: string
  geometry?: string
  length_mm?: number
  height_mm?: number
  internal_diameter_mm?: number
  external_diameter_mm?: number
  total_surface_area_sq_m?: number
  wall_thickness_shell_mm?: number
  wall_thickness_heads_mm?: number
  corrosion_allowance_mm?: number
}

export interface DamageMechanismResult {
  mechanism: string
  rate: string
  justification: string
}

export interface StrategySelectionResult {
  strategy: string
  answer: string
  justification: string
}

export interface RbaAdditionalInfoResult {
  variable: string
  value: string
  source: string
  informs: string
}
export interface CoverageRequirementResult {
  coverage_percentage: GeckoMeasurementJson
  coverage_area: GeckoMeasurementJson
}

export interface NiiData {
  eligibility?: string
  analysis_date?: string
  justification?: string
  inspection_notes?: string
  functional_location?: string
  params?: NiiSourcesParams
  zone_params?: ReadonlyArray<NiiSourcesZoneParams>
  analysis_results?: {
    damage_mechanisms?: DamageMechanismResult[]
    strategy_selection?: StrategySelectionResult[]
    rba_additional_info?: RbaAdditionalInfoResult[]
  }[]
  recommendations?: string[]
  coverage_requirements?: CoverageRequirementResult[]
}

export const NiiDetailsModal: FC<NiiDetailsModalProps> = ({
  isOpen,
  onClose,
  niiEntry,
  siteEntry,
  unitEntry,
}) => {
  const niiData = niiEntry?.data as NiiData | undefined
  const niiId = niiEntry?.id

  const [niiDetailStep, setNiiDetailStep] = useState(0)

  useEffect(() => {
    if (!isOpen) {
      setNiiDetailStep(0)
    }
  }, [isOpen])

  const stepsItems = useMemo(
    () => NII_DETAILS_TABS_MAPPING.map(({ title }) => ({ title })),
    []
  )

  const SelectedStepComponent: FC<NiiViewProps> = useMemo(
    () => NII_DETAILS_TABS_MAPPING[niiDetailStep].component,
    [niiDetailStep]
  )

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      footer={null}
      width='95%'
      title={
        <Title level={4} style={{ margin: 0 }}>
          NII Assessment Details
        </Title>
      }>
      {unitEntry && siteEntry && niiData && niiId ? (
        <>
          <Steps
            style={{ marginBottom: '1rem' }}
            items={stepsItems}
            current={niiDetailStep}
            onChange={setNiiDetailStep}
          />
          <SelectedStepComponent
            key={`${niiId}-${niiDetailStep}`}
            niiId={niiId}
            siteEntry={siteEntry}
            unitEntry={unitEntry}
            niiData={niiData}
          />
          <StyledButtonDiv>
            <Button
              onClick={() => setNiiDetailStep(niiDetailStep - 1)}
              type='primary'
              disabled={niiDetailStep === 0}>
              Previous
            </Button>
            <Button
              onClick={() => setNiiDetailStep(niiDetailStep + 1)}
              type='primary'
              disabled={niiDetailStep === NII_DETAILS_TABS_MAPPING.length - 1}>
              Next
            </Button>
          </StyledButtonDiv>
        </>
      ) : (
        <Title level={4}>No Nii Data Available</Title>
      )}
    </Modal>
  )
}
